import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { useRouter } from 'next/router';
import BaseLayout from 'layouts/BaseLayout';
import GalleryGrid from 'components/GalleryGrid';
import GalleryHomeTitle from 'components/GalleryHomeTitle';
import SearchEmptyState from 'components/SearchEmptyState';
import Breadcrumbs from 'components/Breadcrumbs';
import { useBreadcrumbsContext } from 'contexts/breadcrumbsContext';
import useHasMounted from 'hooks/useHasMounted';
import renderLoaders from 'utils/renderLoaders';
import useTemplatesSearch from 'hooks/useTemplatesSearch';
import { useGalleryContext, GalleryType } from 'contexts/galleryContext';
import TemplatesGrid from 'components/TemplatesGrid';
import buildTemplateImageUrl from 'utils/buildTemplateImageUrl';
import MainContent from 'components/GalleryMainContent/MainContent';
import useIsMobile from 'hooks/useIsMobile';
import { COLOR_PURPLE_HOVER, COLOR_WHITE } from 'styles/variables/colors';
import { setLocale } from 'utils/locale';
import capitalizeString from 'utils/capitalizeString';

import en from './locales/en';
import ja from './locales/en';
import zh from './locales/en';
import es from './locales/es';
import fr from './locales/fr';
import it from './locales/it';

const Props = {
  children: PropTypes.node.isRequired,
};

export default function GalleryLayout({ children, tagsData, onTagClick }) {
  const router = useRouter();
  const [breadcrumbs] = useBreadcrumbsContext();
  const {
    query: { search },
  } = router;
  const hasMounted = useHasMounted();
  const isMobile = useIsMobile();
  const [gallery] = useGalleryContext();
  const [fetchTemplatesSearch, searchTemplatesResults, isTemplatesSearching] = useTemplatesSearch();
  const searchMethod = {
    [GalleryType.Template]: fetchTemplatesSearch,
  };
  const { locale } = router;
  const t = setLocale(locale, { en, es, fr, it, ja, zh });

  function renderBreadcrumbs() {
    return breadcrumbs.length ? (
      <Breadcrumbs>
        {breadcrumbs.map(({ href, label, isDisabled }) => (
          <Breadcrumbs.Item key={uuid()} href={href} isDisabled={isDisabled}>
            {capitalizeString(label)}
          </Breadcrumbs.Item>
        ))}
      </Breadcrumbs>
    ) : null;
  }

  function renderChildren() {
    if (isTemplatesSearching) {
      renderLoaders();
    }

    if (
      gallery.type === GalleryType.Template &&
      searchTemplatesResults &&
      searchTemplatesResults.length > 0 &&
      search
    ) {
      return (
        <>
          <GalleryHomeTitle>{`Results for "${search}"`}</GalleryHomeTitle>
          <GalleryGrid>
            {searchTemplatesResults.map((template) => (
              <TemplatesGrid.Item
                href={`/templates/${template.slug}`}
                key={template.name}
                alt={template.name}
                src={buildTemplateImageUrl(template.slug, template.imagePrefix)}
              />
            ))}
          </GalleryGrid>
        </>
      );
    }

    if (searchTemplatesResults && searchTemplatesResults.length === 0 && search && hasMounted) {
      return (
        <SearchEmptyState
          title="Ooops, no results were found"
          text="Don't worry! You can try searching with different keywords, see our complete list of templates or request a new template."
          buttonText="See all templates"
          onButtonClick={() => router.push(`/gallery`)}
          imageSrc="/images/emptystate.svg"
          imageAlt="Empty State"
        />
      );
    }

    return (
      <>
        {hasMounted && renderBreadcrumbs()}
        {children}
      </>
    );
  }

  useEffect(() => {
    searchMethod[gallery.type];

    window.scrollTo(0, 0);
  }, [search]);

  return (
    <BaseLayout
      bgBrandColor={!isMobile ? COLOR_WHITE : COLOR_PURPLE_HOVER}
      headerWhite={true}
      typeBtn="buttonSecondary"
      hasPadding={false}
    >
      <MainContent renderChildren={renderChildren} tagsData={tagsData} onTagClick={onTagClick} />
    </BaseLayout>
  );
}

GalleryLayout.propTypes = Props;
